import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faPlay } from "@fortawesome/free-solid-svg-icons";
import { changePage } from "../store/actions";

const Widget = () => {
    const affiliateId = localStorage.getItem("affiliateId");
    const type = "floating"; // or "static"
    const horizontalPosition = "right"; // or "left"
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [activeLang, setActiveLang] = useState("javascript");

    const codeSnippets = {
        javascript: `
<div id="instaswap-widget-container"></div>
<script src="https://web.instaswap.com/widget.js"></script>
<script>
  InstaswapWidget.init({
    affiliateId: "${affiliateId}",
    type: "${type}",                    // options: "floating", "static"
    horizontalPosition: "${horizontalPosition}",        // options: "left", "right"
    verticalPosition: "top",           // "top", or "bottom"
    previewText: "Trade Now",         // Only first 10 letters will be shown
    sellAsset: "BTC.BTC",            // optional – default is "BTC.BTC"
    buyAsset: "ETH.ETH",            // optional – default is "ETH.ETH"
    sellAmount: '1'
  });
</script>
    `,
        react: `
import { InstaswapWidget } from 'instaswap-widget';
const App = () => (
  <>
    <div id="instaswap-widget-container"></div>
    <InstaswapWidget 
      affiliateId="${affiliateId}" 
      type="${type}" // options: "floating", "static"
      horizontalPosition="${horizontalPosition}" // options: "left", "right"
      verticalPosition="top" // "top", or "bottom"
      previewText="Trade Now" // Only first 10 letters will be shown
      sellAsset="BTC.BTC" // optional – default is "BTC.BTC"
      buyAsset="ETH.ETH" // optional – default is "ETH.ETH"
      sellAmount="1"
    />
  </>
);
export default App;
    `,
        nextjs: `
import dynamic from 'next/dynamic';
const InstaswapWidget = dynamic(() => import('instaswap-widget'), { ssr: false });
const Home = () => (
  <>
    <div id="instaswap-widget-container"></div>
    <InstaswapWidget 
      affiliateId="${affiliateId}" 
      type="${type}" // options: "floating", "static"
      horizontalPosition="${horizontalPosition}" // options: "left", "right"
      verticalPosition="top" // "top", or "bottom"
      previewText="Trade Now" // Only first 10 letters will be shown
      sellAsset="BTC.BTC" // optional – default is "BTC.BTC"
      buyAsset="ETH.ETH" // optional – default is "ETH.ETH"
      sellAmount="1"
    />
  </>
);
export default Home;
    `,
        vue: `
<template>
  <div id="instaswap-widget-container"></div>
  <InstaswapWidget 
    :affiliateId="${affiliateId}" 
    :type="${type}" // options: "floating", "static"
    :horizontalPosition="${horizontalPosition}" // options: "left", "right"
    verticalPosition="top" // "top", or "bottom"
    previewText="Trade Now" // Only first 10 letters will be shown
    sellAsset="BTC.BTC" // optional – default is "BTC.BTC"
    buyAsset="ETH.ETH" // optional – default is "ETH.ETH"
    sellAmount="1"
  />
</template>
<script>
import InstaswapWidget from 'instaswap-widget';
export default {
  components: { InstaswapWidget }
};
</script>
    `,
        angular: `
import { Component } from '@angular/core';
@Component({
  selector: 'app-root',
  template: '<div id="instaswap-widget-container"></div><instaswap-widget [affiliateId]="${affiliateId}" [type]="${type}" [horizontalPosition]="${horizontalPosition}" verticalPosition="top" previewText="Trade Now" sellAsset="BTC.BTC" buyAsset="ETH.ETH" sellAmount="1"></instaswap-widget>'
})
export class AppComponent {}
    `
    };

    const LANGS = [
        { key: "javascript", label: "JavaScript" },
        { key: "react", label: "React" },
        { key: "nextjs", label: "Next.js" },
        { key: "vue", label: "Vue" },
        { key: "angular", label: "Angular" },
    ];

    const runJavaScriptCode = () => {
        if (window.InstaswapWidget && document.getElementById("instaswap-widget-container").innerHTML.trim() !== "") {
            console.log("Widget already initialized");
            return;
        }

        const container = document.createElement("div");
        container.id = "instaswap-widget-container";

        document.body.appendChild(container);

        const script = document.createElement("script");
        script.src = "https://web.instaswap.com/widget.js?ver=2";
        script.onload = () => {
            if (window.InstaswapWidget) {
                window.InstaswapWidget.init({
                    affiliateId: affiliateId,
                    type: type,
                    horizontalPosition: horizontalPosition,
                    verticalPosition: "bottom",
                    previewText: "Trade Now",
                    sellAsset: "BTC.BTC",
                    buyAsset: "ETH.ETH",
                    sellAmount: '1'
                });
            } else {
                console.error("InstaswapWidget is not defined");
            }
        };
        document.body.appendChild(script);
    };

    const handleWhitelistRedirect = () => {
        dispatch(changePage("whitelisted-websites"));
        navigate("/whitelisted-websites");
    };

    const activeLabel = LANGS.find((l) => l.key === activeLang)?.label;

    return (
        <>
            <Helmet>
                <title>Widget | Instaswap Partners</title>
            </Helmet>
            <div>
                <div className="secHdr">
                    <h2 className="secTitle">Widget Integration</h2>
                    <p className="secLead">
                        Embed the InstaSwap widget directly into your website or
                        application so your users can swap cryptocurrencies without
                        leaving your platform, and you earn on every swap.
                    </p>
                </div>

                <div className="wgGrid">
                    <div>
                        <div className="wgSteps">
                            <div className="wgStep">
                                <span className="wgStepN">1</span>
                                <div>
                                    <div className="wgStepT">Select your language</div>
                                    <div className="wgStepD">
                                        Copy the snippet for your stack: vanilla JS or a framework
                                        integration.
                                    </div>
                                </div>
                            </div>
                            <div className="wgStep">
                                <span className="wgStepN">2</span>
                                <div>
                                    <div className="wgStepT">Drop in the embed</div>
                                    <div className="wgStepD">
                                        Add the container and script, configure your affiliate ID
                                        and default assets. Detailed comments are included in each
                                        snippet.
                                    </div>
                                </div>
                            </div>
                            <div className="wgStep">
                                <span className="wgStepN">3</span>
                                <div>
                                    <div className="wgStepT">Earn on every swap</div>
                                    <div className="wgStepD">
                                        Like our referral program, you earn Bitcoin for each
                                        transaction your users make via the widget.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btnPrimary"
                            style={{ marginTop: 18 }}
                            onClick={handleWhitelistRedirect}
                        >
                            <FontAwesomeIcon icon={faLink} />
                            Whitelist your websites
                        </button>
                        <p className="wgNote">
                            Before going live, whitelist the URLs of the sites where the
                            widget will run. Requests from non-whitelisted origins are
                            rejected.
                        </p>
                    </div>

                    <div className="wgCode">
                        <div id="instaswap-widget-container"></div>
                        <div className="wgTabs">
                            {LANGS.map((lang) => (
                                <button
                                    key={lang.key}
                                    type="button"
                                    className={`wgTab${activeLang === lang.key ? " on" : ""}`}
                                    onClick={() => setActiveLang(lang.key)}
                                >
                                    {lang.label}
                                </button>
                            ))}
                        </div>
                        <pre className="wgPre">
                            <code>{codeSnippets[activeLang]}</code>
                        </pre>
                        <div style={{ padding: "0 18px 18px" }}>
                            <button
                                type="button"
                                className="btnGhost sm accent"
                                onClick={runJavaScriptCode}
                            >
                                <FontAwesomeIcon icon={faPlay} />
                                Run {activeLabel} code
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Widget;
